/**
 * Precondition Evaluators for Gibbs Action Registry
 *
 * Maps symbolic precondition check IDs to evaluator functions.
 * Each evaluator takes a workspace state object and returns boolean.
 *
 * The workspace state is expected to have these methods/properties
 * (matching BMaps App.Workspace API):
 *   - firstCaseData() → CaseData | null
 *   - allCaseData() → CaseData[]  (NOT getCaseDatas — that doesn't exist)
 *   - getLoadedCompounds() → Compound[]  (NOT getCompounds — same)
 *   - getLoadedProteins() → MapCase[]
 *   - getActiveCompound() → Compound | null
 *
 * CaseData is expected to have:
 *   - mapCase → MapCase
 *   - getFragmentInfos() → FragInfo[]
 *   - getSolute() → AtomGroup | null
 *
 * @module gibbs/registry/preconditions
 */

/**
 * @typedef {Object} WorkspaceState
 * Minimal interface for precondition checking.
 * Can be a real App.Workspace or a test stub.
 */

export function fragmentAvailabilityEvidence(caseData) {
    let saved = null;
    try {
        saved = caseData?.getSavedFragmentAvailability?.();
        if (saved != null) {
            const hasValidShape = saved.source === 'saved-state-fragment-info'
                && (
                    (saved.status === 'available'
                        && Number.isInteger(saved.entryCount)
                        && saved.entryCount > 0)
                    || (saved.status === 'unavailable' && saved.entryCount === 0)
                    || (saved.status === 'unknown' && saved.entryCount === null)
                );
            if (!hasValidShape) {
                return {
                    status: 'unknown',
                    source: 'saved-state-fragment-info',
                    entryCount: null,
                };
            }
            // An explicit empty inventory is a deliberate workspace constraint:
            // it remains unavailable even if the target's general catalog later
            // contains fragments.
            if (saved.status === 'unavailable') return { ...saved };
            if (saved.status === 'unknown') return { ...saved };
        }
    } catch (_) {
        return {
            status: 'unknown',
            source: 'saved-state-fragment-info',
            entryCount: null,
        };
    }

    let liveList;
    try {
        liveList = typeof caseData?.getAvailableFragmentInfo === 'function'
            ? caseData.getAvailableFragmentInfo()
            : caseData?.availableFragmentInfo;
    } catch (_) {
        liveList = null;
    }
    if (liveList && typeof liveList.isInitialized === 'function'
        && liveList.isInitialized() !== true) {
        return {
            status: 'unknown',
            source: saved ? 'saved-live-fragment-info' : 'workspace-fragment-info',
            entryCount: null,
        };
    }

    let liveItems;
    const readers = [
        () => liveList?.items?.(),
        () => (typeof caseData?.getFragmentInfos === 'function'
            ? caseData.getFragmentInfos()
            : undefined),
    ];
    for (const read of readers) {
        try {
            const items = read();
            if (Array.isArray(items)) {
                liveItems = items;
                break;
            }
        } catch (_) {
            // Try the remaining supported live workspace representations.
        }
    }
    if (!Array.isArray(liveItems)) {
        return {
            status: 'unknown',
            source: saved ? 'saved-live-fragment-info' : 'workspace-fragment-info',
            entryCount: null,
        };
    }

    if (saved?.status === 'available') {
        let savedKeys = null;
        try {
            savedKeys = caseData?.getSavedFragmentKeys?.();
        } catch (_) {
            savedKeys = null;
        }
        const liveKeys = new Set(liveItems
            .map((item) => item?.key || item?.name)
            .filter((key) => typeof key === 'string' && key.length > 0));
        const identitiesAgree = Array.isArray(savedKeys)
            ? savedKeys.length === saved.entryCount
                && new Set(savedKeys).size === savedKeys.length
                && savedKeys.every((key) => liveKeys.has(key))
            : liveItems.length >= saved.entryCount;
        if (liveItems.length > 0 && identitiesAgree) {
            return {
                status: 'available',
                source: 'saved-live-fragment-info',
                entryCount: saved.entryCount,
            };
        }
        return {
            status: 'unknown',
            source: 'saved-live-fragment-info',
            entryCount: null,
        };
    }
    return {
        status: liveItems.length > 0 ? 'available' : 'unavailable',
        source: 'workspace-fragment-info',
        entryCount: liveItems.length,
    };
}

/**
 * Registry of precondition evaluator functions.
 *
 * @type {Object<string, function(WorkspaceState): boolean>}
 */
export const preconditionEvaluators = {
    /**
     * At least one protein structure is loaded in the workspace.
     */
    protein_loaded(state) {
        const caseData = state.firstCaseData?.();
        return caseData != null && caseData.getSolute?.() != null;
    },

    /**
     * At least one compound (ligand) is loaded in the workspace.
     */
    compound_loaded(state) {
        const compounds = state.getLoadedCompounds?.();
        return Array.isArray(compounds) && compounds.length > 0;
    },

    /**
     * At least one compound has 3D coordinates (not just SMILES).
     */
    compound_has_3d(state) {
        const compounds = state.getLoadedCompounds?.();
        if (!Array.isArray(compounds) || compounds.length === 0) return false;
        return compounds.some((c) => c.getAtoms?.()?.length > 0);
    },

    /**
     * A fragment map (Boltzmann Map) is available for the active protein.
     * A saved non-empty inventory must be reconciled with the initialized live
     * catalog. Explicit saved emptiness remains an authoritative constraint.
     */
    fragment_map_available(state) {
        const caseData = state.firstCaseData?.();
        if (!caseData) return false;
        return fragmentAvailabilityEvidence(caseData).status === 'available';
    },

    /**
     * A binding site is defined (protein has a known binding cavity).
     */
    binding_site_defined(state) {
        const caseData = state.firstCaseData?.();
        return caseData?.getBindingSite?.() != null;
    },

    /**
     * At least two proteins are loaded (for multi-protein operations).
     */
    multiple_proteins_loaded(state) {
        const proteins = state.getLoadedProteins?.();
        return Array.isArray(proteins) && proteins.length >= 2;
    },

    /**
     * There is an active (focused) compound in the workspace.
     */
    active_compound_set(state) {
        return state.getActiveCompound?.() != null;
    },

    /**
     * There is an active atom selection on the server.
     */
    selection_active(state) {
        return state.hasSelection?.() === true;
    },

    /**
     * The workspace has saved state data (non-empty).
     */
    workspace_has_data(state) {
        const caseData = state.firstCaseData?.();
        return caseData != null;
    },

    /**
     * Internet access is available (for external data retrieval).
     * Always true in browser context; can be overridden for testing.
     */
    internet_available(/* state */) {
        return true;
    },

    /**
     * Job manager service is available and connected.
     */
    jobman_available(state) {
        return state.isJobmanAvailable?.() !== false;
    },

    /**
     * Fragment data service (BMapsDS) is available.
     */
    fragment_data_available(state) {
        return state.isFragmentDataAvailable?.() !== false;
    },

    /**
     * Compounds have energy data calculated.
     */
    compound_has_energies(state) {
        const compounds = state.getLoadedCompounds?.();
        if (!Array.isArray(compounds) || compounds.length === 0) return false;
        return compounds.some((c) => c.getEnergies?.() != null);
    },

    /**
     * A simulation spec has been validated and is ready.
     */
    simulation_ready(state) {
        return state.isSimulationReady?.() === true;
    },
};

/**
 * Evaluate a single precondition check against workspace state.
 *
 * @param {string} checkId - Symbolic check ID
 * @param {WorkspaceState} state - Workspace state to check against
 * @returns {boolean} Whether the precondition is met
 * @throws {Error} If checkId is not recognized
 */
export function evaluatePrecondition(checkId, state) {
    const evaluator = preconditionEvaluators[checkId];
    if (!evaluator) {
        throw new Error(`Unknown precondition check: "${checkId}". Valid checks: ${Object.keys(preconditionEvaluators).join(', ')}`);
    }
    return evaluator(state);
}

/**
 * Get all known precondition check IDs.
 *
 * @returns {string[]}
 */
export function getAllPreconditionIds() {
    return Object.keys(preconditionEvaluators);
}
